import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import api, { getActiveRegistration, setActiveRegistration } from "../lib/api";
import { clearAppCacheAndReload } from "../lib/appRefresh";
import CreditMeter from "./CreditMeter";
import CopyGuard from "./CopyGuard";
import { toast } from "sonner";
import {
  SquaresFour, ListChecks, FileText, ShieldCheck, Folders,
  Notebook, CheckSquare, WarningOctagon, SealCheck, Gear, SignOut, Stack, BookOpen, Bell, UserFocus, ClipboardText, Question, LockKey, Buildings, CaretDown, ShieldStar, CalendarCheck, Gauge, Lightning, Kanban, ArrowsClockwise, MagnifyingGlass, CaretDoubleDown, CaretDoubleUp, UsersThree, Handshake, IdentificationBadge, ClockCountdown, Siren, Eye, ArrowsLeftRight, ChartBar, Scales, Alarm, Gavel, List, X,
} from "@phosphor-icons/react";

// Pinned items render flat at the top (no parent group) — the four daily
// workflow drivers. Everything else lives in the lifecycle-ordered groups below.
const PINNED = [
  { to: "/", label: "Home", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/my-assignments", label: "My Assignments", icon: UserFocus, testid: "nav-my-assignments" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
];

// Categorised navigation, arranged strictly by program maturity. `show` gates by
// role; `sub: true` renders an indented sub-item; `soon` renders a disabled
// "Coming Soon" item; a group with `cip: true` is locked (closed-lock) for users
// without CIP/BCSI access.
const CATEGORIES = [
  {
    id: "horizon", label: "Standards Horizon & Readiness",
    items: [
      { to: "/sdp", label: "Standards Dev Process", icon: Scales, testid: "nav-sdp", module: "sdp" },
      { to: "/gaps", label: "Readiness Gaps", icon: WarningOctagon, testid: "nav-gaps", module: "readiness_gaps" },
      { to: "/star", label: "STaR Assessment", icon: ArrowsClockwise, testid: "nav-star", module: "star", sub: true },
      { to: "/readiness-evidence", label: "Readiness Evidence Locker", icon: Folders, testid: "nav-readiness-evidence", module: "readiness_evidence" },
    ],
  },
  {
    id: "design", label: "Program Design",
    items: [
      { to: "/claroflow", label: "ClaroFlow", icon: Kanban, testid: "nav-claroflow", module: "claroflow" },
      { to: "/build", label: "Scope & Applicability", icon: Stack, testid: "nav-build" },
      { to: "/requirements", label: "Standards Library", icon: ListChecks, testid: "nav-requirements" },
      { to: "/cfr", label: "CFR Matrix", icon: UsersThree, testid: "nav-cfr", module: "cfr" },
      { to: "/controls", label: "Internal Controls", icon: ShieldCheck, testid: "nav-controls", module: "internal_controls" },
      { to: "/risk-matrix", label: "Risk Matrix", icon: Gauge, testid: "nav-risk-matrix", module: "risk_matrix" },
    ],
  },
  {
    id: "library", label: "Program Library",
    items: [
      { to: "/manuals", label: "Policy Manuals", icon: BookOpen, testid: "nav-manuals", module: "policy_manuals" },
      { to: "/sop-forms", label: "SOPs & Forms", icon: ClipboardText, testid: "nav-sop-forms", module: "sop_forms" },
      { to: "/required-docs", label: "Required Documents", icon: ClipboardText, testid: "nav-required-docs", module: "required_docs" },
      { to: "/documents?family=op", label: "Documents (5 P's)", icon: FileText, testid: "nav-documents", module: "documents" },
      { to: "/documents?family=cip", label: "CIP Document Library", icon: BookOpen, testid: "nav-cip4p", module: "cip4p" },
      { to: "/documents?status=draft", label: "Proposed 5 P's Drafts", icon: FileText, testid: "nav-proposed-5ps", module: "proposed_5ps" },
    ],
  },
  {
    id: "cip", label: "CIP Workspace", cip: true,
    items: [
      { to: "/cipwatch", label: "CIPWatch", icon: ShieldStar, testid: "nav-ciptrack", module: "ciptrack" },
      { to: "/cip002", label: "CIP-2 ID & Cat", icon: Stack, testid: "nav-cip002", module: "cip002" },
      { to: "/cip004", label: "CIP-4 NERC Access", icon: IdentificationBadge, testid: "nav-cip004", module: "cip004" },
      { to: "/claropatch", label: "CIP-7 & 10 ClaroPatch", icon: Lightning, testid: "nav-claropatch", module: "claropatch" },
      { to: "/scrm", label: "CIP-013 SCRM", icon: Handshake, testid: "nav-scrm", module: "scrm" },
      { to: "/ert", label: "CIP Evidence Request Tool", icon: ShieldStar, testid: "nav-ert", module: "ert" },
      { label: "CIP-7 R1 Ports & Services", icon: Gauge, testid: "nav-cip007-ports", soon: true, module: "soon_cip007_ports" },
      { label: "CIP-7 R3/R4/R5 Security Controls", icon: ShieldStar, testid: "nav-cip007-controls", soon: true, module: "soon_cip007_r345" },
      { label: "CIP-10 R2/R3 Monitoring", icon: ClockCountdown, testid: "nav-cip010-monitoring", soon: true, module: "soon_cip010_r23" },
    ],
  },
  {
    id: "onp", label: "O&P Workspace",
    items: [
      { to: "/opwatch", label: "OPWatch", icon: CalendarCheck, testid: "nav-optrack", module: "optrack" },
      { to: "/prc005", label: "PRC-5 PSMP Maint", icon: Lightning, testid: "nav-prc005", module: "prc005" },
      { to: "/eop004", label: "EOP-4 Event Reporting", icon: Siren, testid: "nav-eop004", module: "eop004" },
      { label: "FAC-3 Vegetation Management", icon: Stack, testid: "nav-fac003", soon: true, module: "soon_fac003" },
      { label: "FAC-8 Facility Ratings", icon: Gauge, testid: "nav-fac008", soon: true, module: "soon_fac008" },
      { label: "PRC-4 Misoperations", icon: WarningOctagon, testid: "nav-prc004", soon: true, module: "soon_prc004" },
      { label: "PER Personnel Training", icon: BookOpen, testid: "nav-per", soon: true, module: "soon_per_training" },
    ],
  },
  {
    id: "assurance", label: "Engagement & Audit Readiness",
    items: [
      { to: "/engagements", label: "Engagements (CMEP)", icon: Gavel, testid: "nav-engagements", module: "engagements" },
      { to: "/risk-assessments", label: "Inherent Risk Assessment", icon: ClipboardText, testid: "nav-ira", module: "ira" },
      { to: "/rsaw", label: "RSAW Workspace", icon: Notebook, testid: "nav-rsaw", module: "rsaw" },
      { to: "/evidence", label: "Evidence Repository", icon: Folders, testid: "nav-evidence", module: "evidence_repository" },
      { to: "/claro-lens", label: "ClaroLens", icon: ListChecks, testid: "nav-rapid-reviews", show: "compliance", module: "rapid_reviews" },
      { to: "/doc-checker", label: "Document Checker", icon: ArrowsLeftRight, testid: "nav-doc-checker", module: "doc_checker" },
      { to: "/data-analyzer", label: "Data Analyzer", icon: ChartBar, testid: "nav-data-analyzer", module: "data_analyzer" },
      { to: "/rfi", label: "RFI Management", icon: Question, testid: "nav-rfi", module: "rfi" },
      { to: "/approvals", label: "Approvals", icon: SealCheck, testid: "nav-approvals", module: "approvals" },
    ],
  },
  {
    id: "enforcement", label: "Enforcement & Remediation",
    items: [
      { to: "/incidents", label: "Incident Tracker", icon: Alarm, testid: "nav-incidents", module: "incident_tracker" },
      { to: "/investigations", label: "Investigations", icon: MagnifyingGlass, testid: "nav-investigations", show: "investigator", module: "investigations" },
      { to: "/pnc-tracker", label: "PNC Tracker", icon: WarningOctagon, testid: "nav-pnc-tracker", module: "pnc_tracker" },
      { to: "/sanction-calculator", label: "Sanction Calculator", icon: Scales, testid: "nav-sanction", module: "sanction_calculator" },
    ],
  },
  {
    id: "reference", label: "Reference & Admin",
    items: [
      { to: "/nerc-library", label: "NERC Library", icon: Folders, testid: "nav-nerc-library" },
      { to: "/guide", label: "User Guide", icon: BookOpen, testid: "nav-guide" },
      { to: "/admin", label: "User & Entity Settings", icon: Gear, testid: "nav-admin", show: "admin" },
    ],
  },
  {
    id: "claro-console", label: "ClaroConsole", superadmin: true,
    items: [
      { to: "/console", label: "Tenant Control Panel", icon: Buildings, testid: "nav-console", show: "superadmin" },
      { to: "/console/manuals", label: "Manuals", icon: BookOpen, testid: "nav-console-manuals", show: "superadmin" },
      { to: "/console/staff-guide", label: "Staff Guide", icon: BookOpen, testid: "nav-staff-guide", show: "superadmin" },
    ],
  },
];

const STORAGE_KEY = "claro-sidebar-groups";
const loadStored = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; }
};
// On first visit (no stored state), expand every top-level nav group so
// items like "ClaroLens" under Audit Readiness are immediately visible.
const defaultOpen = () => Object.fromEntries(CATEGORIES.map((c) => [c.id, true]));

// Stateless render helpers hoisted to module scope so React doesn't remount
// the subtree on every parent render (fixes react/no-unstable-nested-components).
function NavItem({ n, locked, isActive, unread }) {
  const Icon = n.icon;
  const tierLock = !!n.locked && !locked && !n.soon;
  const disabled = n.soon || locked || n.locked;
  const pad = n.sub ? "pl-12 pr-5" : "pl-8 pr-5";
  if (disabled) {
    return (
      <div data-testid={n.testid}
        title={n.soon ? "Coming soon" : tierLock ? "ClaroShield feature — upgrade to unlock" : "Requires CIP / BCSI access"}
        className={`flex items-center gap-3 ${pad} py-2 text-sm cursor-not-allowed select-none ${tierLock ? "text-amber-200/50" : "text-white/30"}`}>
        <Icon size={17} />
        <span className="flex-1">{n.label}</span>
        {n.soon
          ? <span className="text-[9px] font-bold uppercase tracking-wide bg-white/10 text-white/50 rounded px-1.5 py-0.5">Soon</span>
          : tierLock
            ? <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-400/15 text-amber-300/80 rounded px-1.5 py-0.5">Shield</span>
            : <LockKey size={13} weight="fill" className="text-white/40" />}
      </div>
    );
  }
  const activeNow = isActive(n.to);
  return (
    <Link to={n.to} data-testid={n.testid}
      className={`flex items-center gap-3 ${pad} py-2 text-sm transition-colors ${
        activeNow ? "bg-white text-black font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
      }`}>
      <Icon size={n.sub ? 15 : 17} weight={activeNow ? "bold" : "regular"} />
      <span className="flex-1">{n.label}</span>
      {n.wip && <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-400/20 text-amber-300 rounded px-1.5 py-0.5">Soon</span>}
      {n.to === "/notifications" && unread > 0 && (
        <span data-testid="nav-notifications-badge" className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{unread}</span>
      )}
    </Link>
  );
}

function Group({ cat, isOpen, hasActive, locked, onToggle, isActive, unread }) {
  return (
    <div data-testid={`navgroup-${cat.id}`}>
      <button
        type="button"
        onClick={() => onToggle(cat.id)}
        data-testid={`navgroup-toggle-${cat.id}`}
        aria-expanded={isOpen}
        className={`w-full flex items-center gap-2 px-5 py-2 overline tracking-wider transition-colors ${
          hasActive ? "text-white" : "text-white/45 hover:text-white/80"
        }`}>
        <CaretDown size={12} weight="bold"
          className={`transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"}`} />
        <span className="flex-1 text-left">{cat.label}</span>
        {locked && <LockKey size={12} weight="fill" className="text-white/40" />}
      </button>
      {/* grid-rows 0fr->1fr gives a smooth height animation without measuring */}
      <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="pb-1">
            {cat.items.map((n) => <NavItem key={n.testid} n={n} locked={locked} isActive={isActive} unread={unread} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

// Maps a route to a keyword that appears in the user guide's section headings,
// so the contextual Help button deep-links (via /guide?q=) to the right section.
const _GUIDE_KEYWORDS = [
  ["/cfr", "CFR Matrix"], ["/risk-assessments", "Inherent Risk"], ["/star", "STaR"],
  ["/claro-lens", "ClaroLens"], ["/manuals", "Policy Manuals"], ["/sop-forms", "SOPs & Forms"],
  ["/gaps", "Readiness Gaps"], ["/required-docs", "Required Documents"], ["/scrm", "SCRM"],
  ["/doc-checker", "Document Checker"], ["/data-analyzer", "Data Analyzer"], ["/sdp", "SME"],
  ["/console", "ClaroConsole"], ["/admin", "User & Access"],
];
function guideKeywordForPath(pathname, search) {
  if (pathname.startsWith("/documents")) {
    return (search || "").includes("family=cip") ? "CIP Document Library" : "Documents (5 P's)";
  }
  const hit = _GUIDE_KEYWORDS.find(([p]) => pathname === p || pathname.startsWith(p + "/"));
  return hit ? hit[1] : "";
}


export default function Layout({ children }) {
  const { user, logout, isRole, isInvestigator, hasCipAccess } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [hiddenModules, setHiddenModules] = useState([]);
  const [updateReady, setUpdateReady] = useState(false);

  // Auto-detect a new deploy: snapshot the build id at load, poll every 2 min
  // (and on window focus), and prompt a one-click cache-busting reload when it
  // changes — so users and customers never clear cookies/cache to get the latest.
  useEffect(() => {
    let initial = null;
    const check = async () => {
      try {
        const { data } = await api.get("/version");
        const bid = data?.build_id;
        if (!bid) return;
        if (initial === null) { initial = bid; return; }
        if (bid !== initial) setUpdateReady(true);
      } catch { /* ignore transient errors */ }
    };
    check();
    const timer = setInterval(check, 120000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, []);

  // Fetch per-tenant module visibility once per session. Silent failure is fine
  // (falls back to "show everything" which matches historical behavior).
  // Claro super-admin "View as customer": mirror the ACTIVE registered entity's exact
  // experience (plan tier gating + admin-hidden modules). The ClaroConsole stays reachable
  // as the escape hatch. Toggled from the Console; synced app-wide via a custom event.
  const [viewAsCustomer, setViewAsCustomer] = useState(() => localStorage.getItem("claro-view-as-customer") === "1");
  const impersonating = !!user?.is_superadmin && viewAsCustomer;
  useEffect(() => {
    const sync = () => setViewAsCustomer(localStorage.getItem("claro-view-as-customer") === "1");
    window.addEventListener("claro:view-as-customer-changed", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("claro:view-as-customer-changed", sync); window.removeEventListener("storage", sync); };
  }, []);
  const exitViewAsCustomer = () => { localStorage.setItem("claro-view-as-customer", "0"); window.dispatchEvent(new Event("claro:view-as-customer-changed")); };

  // Time/action tracking: fire a lightweight page-view beacon on each route change.
  useEffect(() => {
    if (!user) return;
    api.post("/activity/track", { path: loc.pathname, event: "page_view" }).catch(() => {});
  }, [loc.pathname, user]);

  // Surface the "Entity View is read-only" block (super-admin impersonation) as a toast.
  useEffect(() => {
    const onRO = () => toast.error("Entity View is read-only. Exit Entity View to make changes for this customer.");
    window.addEventListener("claro:entity-view-readonly", onRO);
    return () => window.removeEventListener("claro:entity-view-readonly", onRO);
  }, []);

  const [tierLocked, setTierLocked] = useState([]);
  useEffect(() => {
    if (!user) return;
    const q = impersonating ? "/session/modules?view_as_customer=1" : "/session/modules";
    api.get(q)
      .then((r) => { setHiddenModules(r.data?.hidden_modules || []); setTierLocked(r.data?.tier_locked || []); })
      .catch(() => { setHiddenModules([]); setTierLocked([]); });
  }, [user?.id, user?.active_registration_id, impersonating]);

  // Upcoming ("Soon") modules and Shield-only ("tier-locked") previews are always hidden
  // from the customer view (ClaroShield previews are not ready for primetime).
  const showUpcoming = false;
  const showLocked = false;
  const isTierLocked = useCallback((item) => !!item.module && tierLocked.includes(item.module), [tierLocked]);

  // Build the role- and module-filtered category list (drop empty groups).
  const categories = useMemo(() => {
    const can = (item) =>
      (!item.show || (item.show === "investigator" && isInvestigator) || (item.show === "admin" && isRole("admin")) ||
       (item.show === "superadmin" && user?.is_superadmin) ||
       (item.show === "compliance" && isRole("admin", "analyst"))) &&
      (!item.module || !hiddenModules.includes(item.module)) &&
      (showUpcoming || !item.soon) &&
      (showLocked || !isTierLocked(item));
    return CATEGORIES
      .map((c) => ({ ...c, items: c.items.filter(can).map((it) => (isTierLocked(it) ? { ...it, locked: true } : it)) }))
      .filter((c) => c.items.length > 0);
  }, [isInvestigator, user?.role, user?.is_superadmin, hiddenModules, showUpcoming, showLocked, isTierLocked]);

  // Pinned nav items also respect module hiding + tier lock
  const visiblePinned = useMemo(
    () => PINNED.filter((n) => (!n.module || !hiddenModules.includes(n.module)) && (showLocked || !isTierLocked(n)))
      .map((n) => (isTierLocked(n) ? { ...n, locked: true } : n)),
    [hiddenModules, showLocked, isTierLocked],
  );

  // Route guard: map each nav route to its module, and if the current route belongs
  // to a hidden or tier-locked module, redirect home (blocks direct-URL access on
  // ClaroLight even while previewing). Super-admins never hit this.
  // NOTE: keyed by the FULL `to` (incl. query) because several items share the same
  // path (e.g. /documents?family=op vs ?family=cip) but map to different modules —
  // keying by pathname alone collapsed them and wrongly bounced Documents (5 P's) home.
  const routeModuleMap = useMemo(() => {
    const m = {};
    [...PINNED, ...CATEGORIES.flatMap((c) => c.items)].forEach((it) => {
      if (it.to && it.module) m[it.to] = it.module;
    });
    return m;
  }, []);
  useEffect(() => {
    if (!user || (hiddenModules.length === 0 && tierLocked.length === 0)) return;
    const full = loc.pathname + (loc.search || "");
    const mod = routeModuleMap[full];
    if (mod && (hiddenModules.includes(mod) || tierLocked.includes(mod))) {
      toast.error("That's a ClaroShield feature — not included in your ClaroShield Light plan.");
      nav("/", { replace: true });
    }
  }, [loc.pathname, loc.search, hiddenModules, tierLocked, routeModuleMap, user, nav]);

  const active = (to) => {
    const p = (to || "").split("?")[0];
    return p === "/" ? loc.pathname === "/" : loc.pathname.startsWith(p);
  };
  const activeCatId = useMemo(() => {
    const c = categories.find((cat) => cat.items.some((it) => it.to && active(it.to)));
    return c?.id || null;
  }, [categories, loc.pathname]);

  // Accordion open-state, persisted to localStorage.
  const [open, setOpen] = useState(() => loadStored() || defaultOpen());
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [loc.pathname]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(open)); }, [open]);
  // Always reveal the group containing the active route (incl. on first load).
  useEffect(() => {
    if (activeCatId) setOpen((o) => (o[activeCatId] ? o : { ...o, [activeCatId]: true }));
  }, [activeCatId]);

  const expandAll = () => setOpen(Object.fromEntries(categories.map((c) => [c.id, true])));
  const collapseAll = () => setOpen({});
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const loadUnread = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, []);
  useEffect(() => { loadUnread(); }, [loadUnread, loc.pathname]);
  useEffect(() => {
    window.addEventListener("notifications-updated", loadUnread);
    return () => window.removeEventListener("notifications-updated", loadUnread);
  }, [loadUnread]);

  const [overdue, setOverdue] = useState(0);
  const [flowDismissed, setFlowDismissed] = useState(() => sessionStorage.getItem("flow-overdue-dismissed") === "1");
  const loadOverdue = useCallback(() => {
    // Only tenants whose plan includes ClaroFlow have this endpoint; on ClaroLight/Pro
    // it 402s. Skip the poll entirely, and mark it __silent402 as a belt-and-suspenders
    // so a routine background poll never pops the global upgrade modal.
    if (hiddenModules.includes("claroflow") || tierLocked.includes("claroflow")) { setOverdue(0); return; }
    api.get("/flow/my-work", { __silent402: true }).then((r) => setOverdue(r.data.overdue || 0)).catch(() => {});
  }, [hiddenModules, tierLocked]);
  useEffect(() => { loadOverdue(); }, [loadOverdue, loc.pathname]);
  const dismissFlow = () => { sessionStorage.setItem("flow-overdue-dismissed", "1"); setFlowDismissed(true); };

  const [regs, setRegs] = useState([]);
  const [activeReg, setActiveReg] = useState(getActiveRegistration());
  const loadRegs = useCallback(() => {
    api.get("/registrations").then((r) => {
      const list = (r.data.registrations || []).filter((x) => x.member !== false);
      setRegs(list);
      const cur = getActiveRegistration() || r.data.active_registration_id || (list[0] && list[0].id) || "";
      setActiveReg(cur);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadRegs(); }, [loadRegs]);
  // Keep the entity switcher fresh after a rename/create/delete (Admin dispatches
  // the event) and when the tab regains focus.
  useEffect(() => {
    const h = () => loadRegs();
    window.addEventListener("claro:registrations-changed", h);
    window.addEventListener("focus", h);
    return () => {
      window.removeEventListener("claro:registrations-changed", h);
      window.removeEventListener("focus", h);
    };
  }, [loadRegs]);
  const switchReg = (id) => {
    if (!id || id === (getActiveRegistration() || activeReg)) return;
    // Switching organizations requires a fresh login (the active tenant is locked
    // to the session). Confirm, then log out and send them to the selected
    // organization's login page.
    if (user?.is_superadmin) { setActiveRegistration(id); window.location.assign("/"); return; }
    const target = regs.find((r) => r.id === id);
    const label = target?.name || "the selected organization";
    const dest = target?.slug ? `/${target.slug}` : "/login";
    if (!window.confirm(`Switching to ${label} will log you out of your current session. You'll sign back in on ${label}'s login page. Continue?`)) return;
    try { localStorage.removeItem("claro_active_registration"); } catch (e) { /* noop */ }
    Promise.resolve(logout && logout()).catch(() => {}).finally(() => window.location.assign(dest));
  };

  const mainGroups = categories.filter((c) => !c.admin);
  const adminGroups = categories.filter((c) => c.admin);
  const renderGroup = (cat) => (
    <Group
      key={cat.id}
      cat={cat}
      isOpen={!!open[cat.id]}
      hasActive={cat.id === activeCatId}
      locked={!!cat.cip && !hasCipAccess}
      onToggle={toggle}
      isActive={active}
      unread={unread}
    />
  );

  return (
    <div className="flex h-screen bg-background">
      {navOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setNavOpen(false)} data-testid="mobile-nav-backdrop" />}
      <aside data-testid="app-sidebar"
        className={`w-60 shrink-0 border-r border-white/10 flex flex-col bg-[#071B3E] text-white fixed inset-y-0 left-0 z-50 transition-transform duration-200 md:static md:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <button onClick={() => setNavOpen(false)} className="md:hidden absolute top-4 right-3 text-white/70 hover:text-white p-1" aria-label="Close menu" data-testid="mobile-nav-close"><X size={20} /></button>
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          <img src="/claro-shield.png?v=3" alt="Claro Compliance, LLC" className="h-10 w-auto object-contain" />
          <div>
            <div className="font-display font-black text-lg tracking-tight leading-none">CLARO</div>
            <div className="overline text-white/50 mt-1">Compliance</div>
          </div>
        </div>

        {regs.length > 0 && (
          <div className="px-5 py-3 border-b border-white/10" data-testid="registration-switcher">
            <div className="flex items-center gap-1.5 overline text-white/40 mb-1"><Buildings size={12} /> Registered Entity</div>
            <div className="relative">
              <select
                data-testid="registration-select"
                value={activeReg}
                onChange={(e) => switchReg(e.target.value)}
                className="w-full appearance-none bg-white/10 text-white text-sm rounded-none border border-white/15 px-2 py-1.5 pr-7 focus:outline-none focus:border-white/40 cursor-pointer"
              >
                {regs.map((r) => (
                  <option key={r.id} value={r.id} className="text-black">{r.name}{r.nerc_id ? ` (${r.nerc_id})` : ""}</option>
                ))}
              </select>
              <CaretDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Global expand / collapse control */}
        <div className="flex items-center justify-end gap-1 px-3 py-1.5 border-b border-white/10">
          <button type="button" onClick={expandAll} data-testid="sidebar-expand-all"
            title="Expand all groups"
            className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white px-1.5 py-0.5 transition-colors">
            <CaretDoubleDown size={12} weight="bold" /> Expand all
          </button>
          <span className="text-white/15">|</span>
          <button type="button" onClick={collapseAll} data-testid="sidebar-collapse-all"
            title="Collapse all groups"
            className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white px-1.5 py-0.5 transition-colors">
            <CaretDoubleUp size={12} weight="bold" /> Collapse all
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          <div className="pb-2 mb-1 border-b border-white/10" data-testid="nav-pinned">
            {visiblePinned.map((n) => <NavItem key={n.testid} n={n} isActive={active} unread={unread} />)}
          </div>
          {mainGroups.map(renderGroup)}
          {adminGroups.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/10" data-testid="nav-admin-section">
              {adminGroups.map(renderGroup)}
            </div>
          )}
        </nav>

        <div className="border-t border-white/10 p-4">
          <CreditMeter />
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="overline text-white/40">{user?.role}</div>
          <Link to="/account" data-testid="nav-account" className="mt-3 flex items-center gap-2 text-xs text-white/60 hover:text-white">
            <LockKey size={14} /> Account &amp; Security
          </Link>
          <Link to="/billing" data-testid="nav-billing" className="mt-2 flex items-center gap-2 text-xs text-white/60 hover:text-white">
            <Lightning size={14} /> Billing &amp; Plans
          </Link>
          <Link to="/plans" data-testid="nav-compare-plans" className="mt-2 flex items-center gap-2 text-xs text-white/60 hover:text-white">
            <Scales size={14} /> Compare plans
          </Link>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}
            className="mt-2 flex items-center gap-2 text-xs text-white/60 hover:text-white">
            <SignOut size={14} /> Sign out
          </button>
          <button data-testid="refresh-app-btn" onClick={clearAppCacheAndReload}
            title="Clears cached app files and reloads the latest version. Keeps you logged in."
            className="mt-2 flex items-center gap-2 text-xs text-white/60 hover:text-white">
            <ArrowsClockwise size={14} /> Refresh app
          </button>
          <div data-testid="app-copyright" className="mt-3 pt-3 border-t border-white/10 text-[10px] text-white/40">
            © 2026 Claro Compliance, LLC
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden shrink-0 flex items-center gap-3 bg-[#071B3E] text-white px-4 py-3" data-testid="mobile-topbar">
          <button onClick={() => setNavOpen(true)} aria-label="Open menu" className="p-1 -ml-1" data-testid="mobile-nav-open"><List size={24} /></button>
          <img src="/claro-shield.png?v=3" alt="Claro Compliance" className="h-7 w-auto" />
          <span className="font-display font-black tracking-tight">CLARO</span>
        </div>
      <main className="mobile-fit flex-1 overflow-y-auto min-w-0">
        {impersonating && (
          <div data-testid="view-as-customer-banner" className="flex items-center gap-3 bg-indigo-900 text-white px-6 py-2.5 text-sm sticky top-0 z-40 border-b border-indigo-700">
            <Eye size={16} weight="fill" className="shrink-0 text-indigo-300" />
            <span className="flex-1">
              <b>Entity View</b> — you are viewing ClaroShield exactly as <b>{(regs.find((r) => r.id === activeReg) || {}).name || "the selected customer"}</b> sees it (Claro Super Admin). Open <b>ClaroConsole</b> anytime to see everything again.
            </span>
            <button data-testid="exit-view-as-customer" onClick={exitViewAsCustomer}
              className="font-semibold bg-white/15 hover:bg-white/25 px-3 py-1 rounded-none shrink-0">Exit Entity View</button>
          </div>
        )}
        {user?.mfa_enrollment?.required && user.mfa_enrollment.grace_active && (
          <div data-testid="mfa-grace-banner" className="flex items-center gap-3 bg-amber-50 border-b border-amber-300 text-amber-900 px-6 py-2.5 text-sm">
            <LockKey size={16} className="shrink-0" />
            <span className="flex-1">
              Your organization requires multi-factor authentication.
              {typeof user.mfa_enrollment.days_left === "number"
                ? ` Set it up within ${user.mfa_enrollment.days_left} day${user.mfa_enrollment.days_left === 1 ? "" : "s"}`
                : " Set it up soon"} or you won&apos;t be able to sign in.
            </span>
            <Link to="/account" data-testid="mfa-grace-setup-link" className="font-semibold underline shrink-0">Set up MFA</Link>
          </div>
        )}
        {overdue > 0 && !flowDismissed && !loc.pathname.startsWith("/claroflow") && !hiddenModules.includes("claroflow") && (
          <div data-testid="flow-overdue-banner" className="flex items-center gap-3 bg-red-50 border-b border-red-300 text-red-900 px-6 py-2.5 text-sm">
            <WarningOctagon size={16} weight="fill" className="shrink-0 text-red-600" />
            <span className="flex-1">
              You have <b>{overdue}</b> overdue ClaroFlow ticket{overdue === 1 ? "" : "s"} assigned to you.
            </span>
            <Link to="/claroflow" data-testid="flow-overdue-link" className="font-semibold underline shrink-0">View My Work</Link>
            <button data-testid="flow-overdue-dismiss" onClick={dismissFlow} className="text-red-700 hover:text-red-900 shrink-0" aria-label="Dismiss">✕</button>
          </div>
        )}
        {updateReady && (
          <div data-testid="app-update-banner" className="flex items-center gap-3 bg-blue-50 border-b border-blue-300 text-blue-900 px-6 py-2.5 text-sm">
            <ArrowsClockwise size={16} weight="bold" className="shrink-0 text-blue-600" />
            <span className="flex-1">A new version of ClaroShield is available.</span>
            <button data-testid="app-update-reload" onClick={clearAppCacheAndReload} className="font-semibold underline shrink-0">Reload now</button>
            <button data-testid="app-update-dismiss" onClick={() => setUpdateReady(false)} className="text-blue-700 hover:text-blue-900 shrink-0" aria-label="Dismiss">✕</button>
          </div>
        )}
        {!loc.pathname.startsWith("/guide") && !loc.pathname.startsWith("/console/staff-guide") && (
          <Link
            to={`/guide${guideKeywordForPath(loc.pathname, loc.search) ? `?q=${encodeURIComponent(guideKeywordForPath(loc.pathname, loc.search))}` : ""}`}
            data-testid="contextual-help-btn"
            title="Open the user guide for this page"
            className="fixed bottom-5 right-5 z-40 flex items-center gap-1.5 bg-[#071B3E] text-white shadow-lg hover:bg-[#0c2a5e] px-3 py-2 text-sm rounded-none">
            <Question size={16} weight="bold" /> Help
          </Link>
        )}
        {children}
      </main>
      </div>
      <CopyGuard />
    </div>
  );
}
